import React, { useEffect, useMemo, useState } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import {
  Box,
  Typography,
  Grid,
  Card,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  TableContainer,
  Pagination,
  Stack,
  TableSortLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Checkbox,
} from "@mui/material";
import SearchBar from "../../Subcomponents/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckIcon from "@mui/icons-material/Check";
import { useDispatch } from "react-redux";
import actions from "../../Redux Store/actions";
import { getAvailableFYs, getCurrentFY, toNumber } from "../../Services/Payout";
import CpPayoutDrawer from "./CpPayoutDrawerComponent";
import { createClient } from "@supabase/supabase-js";

// ─── Supabase client ──────────────────────────────────────────────────────────
const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

// ─── Theme ────────────────────────────────────────────────────────────────────
// "Inter" (bare) has no @font-face in this app — only Inter-Medium,
// Inter-Regular, Inter-SemiBold, Roboto-Regular and Roboto-Medium are declared
// (App.css:1-24) — so this previously fell through to Segoe UI while the rest of
// the app rendered in Inter. Inter-Medium is the app body font (App.css:31).
// This ThemeProvider wraps CpPayoutDrawerComponent too, so this one line fixes
// the typeface across both files.
const theme = createTheme({
  typography: {
    fontFamily: `Inter-Medium, Inter-Regular, Roboto-Regular, Arial, sans-serif`,
  },
});

// ─── Constants ────────────────────────────────────────────────────────────────
// Palette mapped onto the Leads tab's values so CP Payout reads as the same
// product. Key names are unchanged — CpPayoutDrawerComponent reads them too.
//
// The five numeric-column colours keep their per-column distinction (each
// column stays visually separable in a wide financial table) but every value is
// now a real Leads colour from the chip palette in Leadcard.jsx.
const COLORS = {
  border: "#EAECF0",        // was #e5e7eb — Leads' hairline
  textSecondary: "#667085", // was #6b7280 — Leads' muted text
  blue: "#1570EF",          // was #2563eb — Leads' primary action blue
  blueLight: "#EFF8FF",     // was #dbeafe — Leads' light blue fill
  orange: "#B54708",        // was #f59e0b — Leads' amber
  soldValue: "#0E7090",     // was #0f766e — Leads' cyan
  base: "#1570EF",          // was #1d4ed8 — Leads' primary blue
  aop: "#5925DC",           // was #7c3aed — Leads' violet
  pop: "#B54708",           // was #ea580c — Leads' amber/orange
  total: "#027A48",         // was #059669 — Leads' green
};

const PAGE_SIZE = 9;

// ✅ Supabase .in() filters go out as one big GET query string. With no CP
// selected, allotmentIds can be hundreds/thousands of UUIDs long and the
// request fails outright ("FetchError: Failed to fetch") once the URL blows
// past the network's length limit. Chunk it instead of sending it as one call.
const ID_CHUNK_SIZE = 150;

const chunkArray = (arr, size) => {
  const chunks = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatIndianNumber = (num = 0, decimals = 0) =>
  new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(toNumber(num));

const formatCurrency = (num = 0) => `₹${formatIndianNumber(num, 0)}`;

const formatCrLakh = (num = 0) => {
  const n = toNumber(num);
  if (n >= 1_00_00_000) {
    const val = n / 1_00_00_000;
    return `₹${val % 1 === 0 ? val.toFixed(0) : val.toFixed(2)} Cr`;
  }
  if (n >= 1_00_000) {
    const val = n / 1_00_000;
    return `₹${val % 1 === 0 ? val.toFixed(0) : val.toFixed(2)} L`;
  }
  return `₹${formatIndianNumber(n, 0)}`;
};

const safePercent = (value, total) => {
  if (!total || total <= 0) return 0;
  return Math.min(100, Math.round((toNumber(value) / toNumber(total)) * 100));
};

/**
 * Convert "FY 2025-26" → { start: Date, end: Date } for filtering.
 * FY starts 1 Apr fyStart, ends 31 Mar fyStart+1.
 */
const fyToDateRange = (fyStr) => {
  if (!fyStr || fyStr === "All") return { start: null, end: null };
  // e.g. "FY 2025-26"
  const match = fyStr.match(/FY\s+(\d{4})-(\d{2})/);
  if (!match) return { start: null, end: null };
  const startYear = parseInt(match[1], 10);
  const endYear = startYear + 1;
  return {
    start: new Date(`${startYear}-04-01T00:00:00.000Z`).toISOString(),
    end: new Date(`${endYear}-03-31T23:59:59.999Z`).toISOString(),
  };
};

// ─── StatCard ─────────────────────────────────────────────────────────────────
const StatCard = ({ title, value, percent, iconBg, icon: Icon }) => (
  <Card
    elevation={0}
    sx={{
      p: 2.5,
      borderRadius: 3,
      border: `1px solid ${COLORS.border}`,
      bgcolor: "#fff",
      height: "100%",
    }}
  >
    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
      <Box>
        <Typography
          fontSize={11}
          fontWeight={500}
          color={COLORS.textSecondary}
          letterSpacing={0.5}
          sx={{ textTransform: "uppercase", mb: 1 }}
        >
          {title}
        </Typography>
        <Typography fontWeight={700} fontSize={26} lineHeight={1} mb={0.5}>
          {value}
        </Typography>
      </Box>
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: "50%",
          bgcolor: iconBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 22, color: "#fff" }} />
      </Box>
    </Box>
    <LinearProgress
      variant="determinate"
      value={percent}
      sx={{
        mt: 2,

        height: 5,
        borderRadius: 5,
        bgcolor: COLORS.border,
        "& .MuiLinearProgress-bar": { bgcolor: iconBg },
      }}
    />
  </Card>
);

// ─── Table Headers ────────────────────────────────────────────────────────────
const TABLE_HEADERS = [
  { label: "CP NAME", key: null },
  { label: "SOLD UNITS", key: "units" },
  { label: "SOLD VALUE", key: "value" },
  { label: "BASE", key: "brokerage" },
  { label: "AOP", key: "aop" },
  { label: "POP", key: "pop" },
  { label: "TOTAL", key: "total" },
  { label: "PAID", key: "paid" },
  { label: "ACTION", key: null },
];

// ─── Main Component ───────────────────────────────────────────────────────────
function CpPayout() {
  const dispatch = useDispatch();

  // ── Raw data from RPC ──
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(true);

  // ── Filters ──
  const [search, setSearch] = useState("");
  const [fyFilter, setFyFilter] = useState(() => getCurrentFY());
  const [tempFyFilter, setTempFyFilter] = useState(() => getCurrentFY());
  const [selectedCpIds, setSelectedCpIds] = useState([]);
  const [tempSelectedCpIds, setTempSelectedCpIds] = useState([]);

  // ── Filter UI ──
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterTab, setFilterTab] = useState("CP");
  const [cpFilterSearch, setCpFilterSearch] = useState("");
  const [btnName, setBtnName] = useState("None");

  // ── Table ──
  const [sortBy, setSortBy] = useState("total");
  const [sortOrder, setSortOrder] = useState("desc");
  const [page, setPage] = useState(1);
  const [rpcLoading, setRpcLoading] = useState(false);

  // ── Drawer ──
  const [open, setOpen] = useState(false);
  const [selectedCp, setSelectedCp] = useState(null);
  // ✅ The selected CP's FULL agreement-done bookings straight from Supabase, so
  // the drawer shows every agreement-done lead — including ones with no CP-payment
  // entry yet (getAllBrokerageData omits those). Fetched only when the drawer opens.
  const [cpFullLeads, setCpFullLeads] = useState([]);
  const [enrichmentData, setEnrichmentData] = useState([]);
  const [unitCostData, setUnitCostData] = useState([]);
  // ✅ Fallback source for Status / Brokerage % / TDS when getAllBrokerageData
  // doesn't return them for a record. Sourced from CpPayments (BrokerageType = "Basic").
  const [cpPaymentsFallback, setCpPaymentsFallback] = useState([]);
  // ✅ Project short codes — getAllBrokerageData doesn't return these, sourced
  // from the Projects table keyed by project_id.
  const [projectShortCodeData, setProjectShortCodeData] = useState([]);
  // ✅ Lead-agreement extras (for the "Lead Agreement" drawer tab): Inventory.unitOf
  // (LO/VJ ownership) keyed by unitId, and AgreementForm.agreementStatus (holds
  // agreement_date) keyed by allotmentPaymentId. Booking date + unitId come off
  // the extended AllotmentPayment fetch (unitCostData). All isolated so a failure
  // here never breaks the core payout table.
  const [unitOfData, setUnitOfData] = useState([]);
  const [agreementData, setAgreementData] = useState([]);
  // ── FY options ──
  const fyOptions = useMemo(() => getAvailableFYs(), []);

  // ─────────────────────────────────────────────────────────────────────────────
  // FETCH via Supabase RPC
  // Re-runs when fyFilter or selectedCpIds change (both are "applied" values).
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    dispatch(actions.activeMenu("cppayout"));
  }, [dispatch]);

  useEffect(() => {
    fetchBrokerageData()
  }, [fyFilter, selectedCpIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // When the drawer opens for a CP, pull that CP's full agreement-done booking
  // list from Supabase (independent of getAllBrokerageData).
  useEffect(() => {
    if (open && selectedCp?.cp_id) {
      fetchCpAgreementDoneLeads(selectedCp.cp_id).then(setCpFullLeads);
    } else {
      setCpFullLeads([]);
    }
  }, [open, selectedCp?.cp_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBrokerageData = async () => {
    try {
      setLoading(true);

      const { start, end } = fyToDateRange(fyFilter);

      const payload = {
        projectIds: null,
        cpIds: selectedCpIds?.length ? selectedCpIds : null,
        startDate: start ? new Date(start).getTime() : null,
        endDate: end ? new Date(end).getTime() : null,
        includeAllStatuses: false,
      };

      const response = await fetch(
        `${process.env.REACT_APP_PAYMENT_LINK_URL}api/cp-payment/brokerage-report`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();
      const data = Array.isArray(result?.data) ? result.data : [];

      setAllData(data);

      const allotmentIds = Array.from(
        new Set(
          data
            .map((r) => r.allotmentPaymentId || r.allotment_payment_id)
            .filter(Boolean)
        )
      );

      const projectIds = Array.from(
        new Set(data.map((r) => r.project_id).filter(Boolean))
      );

      // ✅ WAIT for RPC before finishing
      // (allSettled: a hiccup fetching unitCost must never wipe out enrichment/paid data)
      if (allotmentIds.length > 0) {
        await Promise.allSettled([
          fetchEnrichmentData(allotmentIds),
          fetchUnitCostData(allotmentIds),
          fetchCpPaymentsFallback(allotmentIds),
          fetchAgreementData(allotmentIds),
        ]);
      } else {
        setEnrichmentData([]);
        setUnitCostData([]);
        setCpPaymentsFallback([]);
        setUnitOfData([]);
        setAgreementData([]);
      }

      if (projectIds.length > 0) {
        await fetchProjectShortCodes(projectIds);
      } else {
        setProjectShortCodeData([]);
      }

    } catch (err) {
      console.error("❌ fetchBrokerageData failed:", err);
      setAllData([]);
      setEnrichmentData([]);
      setUnitCostData([]);
      setCpPaymentsFallback([]);
      setProjectShortCodeData([]);
    } finally {
      setLoading(false); // ✅ only ends after EVERYTHING done
    }
  };


  const fetchEnrichmentData = async (allotmentIds) => {
    try {
      const { data, error } = await supabase.rpc(
        "get_cp_payment_enrichment",
        {
          p_allotment_ids: allotmentIds,
        }
      );

      if (error) {
        console.error("❌ RPC error:", error);
        return;
      }

      setEnrichmentData(data || []);
    } catch (err) {
      console.error("❌ RPC failed:", err);
    }
  };

  // ✅ Agreement Value comes straight from AllotmentPayment.unitCost.bsp.
  // (CpBookingComputState.booking_value used by the backend RPC lags behind
  // for the current FY, so we source it directly instead of waiting on that table.)
  const fetchUnitCostData = async (allotmentIds) => {
    try {
      const chunks = chunkArray(allotmentIds, ID_CHUNK_SIZE);
      const settled = await Promise.allSettled(
        chunks.map((chunk) =>
          supabase
            .from("AllotmentPayment")
            // unitId + dates added for the Lead Agreement tab (booking date =
            // preselectConfirmationDate || created_at; unitId → Inventory.unitOf).
            .select("allotmentPaymentId, unitCost, unitId, preselectConfirmationDate, created_at")
            .in("allotmentPaymentId", chunk)
        )
      );

      const data = [];
      settled.forEach((result) => {
        if (result.status === "fulfilled" && !result.value.error) {
          data.push(...(result.value.data || []));
        } else {
          console.error(
            "❌ unitCost fetch error (chunk):",
            result.status === "fulfilled" ? result.value.error : result.reason
          );
        }
      });

      setUnitCostData(data);

      // Chain the Inventory.unitOf lookup off the unitIds we just resolved.
      const unitIds = [...new Set(data.map((r) => r.unitId).filter(Boolean))];
      if (unitIds.length) await fetchUnitOfData(unitIds);
      else setUnitOfData([]);
    } catch (err) {
      console.error("❌ unitCost fetch failed:", err);
      setUnitCostData([]);
      setUnitOfData([]);
    }
  };

  // ✅ Inventory.unitOf (LO / VJ ownership) keyed by unitId — Lead Agreement tab.
  const fetchUnitOfData = async (unitIds) => {
    try {
      const chunks = chunkArray(unitIds, ID_CHUNK_SIZE);
      const settled = await Promise.allSettled(
        chunks.map((chunk) =>
          supabase.from("Inventory").select("unitId, unitOf").in("unitId", chunk)
        )
      );
      const data = [];
      settled.forEach((result) => {
        if (result.status === "fulfilled" && !result.value.error) data.push(...(result.value.data || []));
        else console.error("❌ Inventory unitOf fetch error (chunk):", result.status === "fulfilled" ? result.value.error : result.reason);
      });
      setUnitOfData(data);
    } catch (err) {
      console.error("❌ Inventory unitOf fetch failed:", err);
      setUnitOfData([]);
    }
  };

  // ✅ The selected CP's FULL agreement-done bookings (drawer only). Two hops:
  // Leads (of this CP) → AllotmentPayment (status "Agreement Done"). Includes the
  // unit/wing/project + unitOf + agreement/booking dates + unitCost so leads with
  // no CP-payment entry can still be shown. Returns [] on any failure (drawer then
  // falls back to whatever getAllBrokerageData provided).
  const fetchCpAgreementDoneLeads = async (cpId) => {
    try {
      const { data: leads, error: le } = await supabase
        .from("Leads")
        .select("leadId")
        .eq("cpId", cpId);
      if (le) throw le;
      const leadIds = Array.from(new Set((leads || []).map((l) => l.leadId).filter(Boolean)));
      if (!leadIds.length) return [];

      const chunks = chunkArray(leadIds, ID_CHUNK_SIZE);
      const settled = await Promise.allSettled(
        chunks.map((chunk) =>
          supabase
            .from("AllotmentPayment")
            .select(
              `allotmentPaymentId, unitId, unitCost, preselectConfirmationDate, created_at,
               Inventory:unitId ( unitNo, unitOf, wingId, projectId,
                 Projects:projectId ( projectName, projectShortCode ),
                 Wings:wingId ( wingName )
               ),
               AgreementForm!agreementform_allotmentpaymentid_fkey ( agreementStatus, created_at )`
            )
            .eq("status", "Agreement Done")
            .in("leadId", chunk)
        )
      );

      const rows = [];
      settled.forEach((result) => {
        if (result.status === "fulfilled" && !result.value.error) rows.push(...(result.value.data || []));
        else console.error("❌ CP agreement-done fetch error (chunk):", result.status === "fulfilled" ? result.value.error : result.reason);
      });
      return rows;
    } catch (err) {
      console.error("❌ fetchCpAgreementDoneLeads failed:", err);
      return [];
    }
  };

  // ✅ AgreementForm.agreementStatus (JSON holding agreement_date) keyed by allotmentPaymentId.
  const fetchAgreementData = async (allotmentIds) => {
    try {
      const chunks = chunkArray(allotmentIds, ID_CHUNK_SIZE);
      const settled = await Promise.allSettled(
        chunks.map((chunk) =>
          supabase.from("AgreementForm").select("allotmentPaymentId, agreementStatus").in("allotmentPaymentId", chunk)
        )
      );
      const data = [];
      settled.forEach((result) => {
        if (result.status === "fulfilled" && !result.value.error) data.push(...(result.value.data || []));
        else console.error("❌ AgreementForm fetch error (chunk):", result.status === "fulfilled" ? result.value.error : result.reason);
      });
      setAgreementData(data);
    } catch (err) {
      console.error("❌ AgreementForm fetch failed:", err);
      setAgreementData([]);
    }
  };

  // ✅ Fallback for Status / Brokerage % / TDS when getAllBrokerageData doesn't
  // return them for a record. Same allotmentPaymentId lookup as unitCost, but
  // against CpPayments (BrokerageType = "Basic" — that table also holds AOP/POP rows).
  const fetchCpPaymentsFallback = async (allotmentIds) => {
    try {
      const chunks = chunkArray(allotmentIds, ID_CHUNK_SIZE);
      const settled = await Promise.allSettled(
        chunks.map((chunk) =>
          supabase
            .from("CpPayments")
            .select(
              "allotmentPaymentId, Status, Brokerage_Persentage, Brokerage_Amount_TDS, Brokerage_Amount_Basic, Agreement_Value"
            )
            .in("allotmentPaymentId", chunk)
            .eq("BrokerageType", "Basic")
        )
      );

      const data = [];
      settled.forEach((result) => {
        if (result.status === "fulfilled" && !result.value.error) {
          data.push(...(result.value.data || []));
        } else {
          console.error(
            "❌ CpPayments fallback fetch error (chunk):",
            result.status === "fulfilled" ? result.value.error : result.reason
          );
        }
      });

      setCpPaymentsFallback(data);
    } catch (err) {
      console.error("❌ CpPayments fallback fetch failed:", err);
      setCpPaymentsFallback([]);
    }
  };

  // ✅ Project short codes — getAllBrokerageData only returns project_name, so
  // projectShortCode is looked up separately from the Projects table.
  const fetchProjectShortCodes = async (projectIds) => {
    try {
      const chunks = chunkArray(projectIds, ID_CHUNK_SIZE);
      const settled = await Promise.allSettled(
        chunks.map((chunk) =>
          supabase
            .from("Projects")
            .select("projectId, projectShortCode")
            .in("projectId", chunk)
        )
      );

      const data = [];
      settled.forEach((result) => {
        if (result.status === "fulfilled" && !result.value.error) {
          data.push(...(result.value.data || []));
        } else {
          console.error(
            "❌ Projects short code fetch error (chunk):",
            result.status === "fulfilled" ? result.value.error : result.reason
          );
        }
      });

      setProjectShortCodeData(data);
    } catch (err) {
      console.error("❌ Projects short code fetch failed:", err);
      setProjectShortCodeData([]);
    }
  };
  // ─────────────────────────────────────────────────────────────────────────────
  // DERIVED DATA — all useMemo, zero extra API calls
  // ─────────────────────────────────────────────────────────────────────────────

  /** Unique CP list for the filter panel (from ALL data, ignoring CP filter) */
  const allCpOptions = useMemo(() => {
    const map = new Map();
    allData.forEach((rec) => {
      if (rec.cp_id && rec.cp_name) {
        map.set(rec.cp_id, { id: rec.cp_id, name: rec.cp_name });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [allData]);

  /** Aggregate raw records into one row per CP */
  // ✅ FIRST: enrichmentMap
  const enrichmentMap = useMemo(() => {
    const map = new Map();

    (enrichmentData || []).forEach((row) => {
      map.set(row.allotment_payment_id, row);
    });

    return map;
  }, [enrichmentData]);

  // ✅ Project short code map — keyed by project_id, sourced from Projects table
  const projectShortCodeMap = useMemo(() => {
    const map = new Map();

    (projectShortCodeData || []).forEach((row) => {
      map.set(row.projectId, row.projectShortCode);
    });

    return map;
  }, [projectShortCodeData]);

  // ✅ Agreement Value map — keyed by allotmentPaymentId, sourced from
  // AllotmentPayment.unitCost.bsp (always populated, unlike CpBookingComputState)
  const unitCostMap = useMemo(() => {
    const map = new Map();

    (unitCostData || []).forEach((row) => {
      const bsp = toNumber(row?.unitCost?.bsp ?? row?.unitcost?.bsp);
      map.set(row.allotmentPaymentId, bsp);
    });

    return map;
  }, [unitCostData]);

  // ✅ unitId → unitOf (ownership), and allotmentPaymentId → { unitId, bookingDate }
  // and allotmentPaymentId → agreement_date. All for the Lead Agreement tab.
  const unitOfMap = useMemo(() => {
    const map = new Map();
    (unitOfData || []).forEach((row) => map.set(row.unitId, row.unitOf));
    return map;
  }, [unitOfData]);

  const bookingMetaMap = useMemo(() => {
    const map = new Map();
    (unitCostData || []).forEach((row) => {
      map.set(row.allotmentPaymentId, {
        unitId: row.unitId,
        bookingDate: row.preselectConfirmationDate || row.created_at || null,
      });
    });
    return map;
  }, [unitCostData]);

  const agreementDateMap = useMemo(() => {
    const map = new Map();
    (agreementData || []).forEach((row) => {
      const d = row?.agreementStatus?.agreement_date || null;
      // Prefer an entry that actually carries a date if duplicates exist.
      if (!map.get(row.allotmentPaymentId) || d) map.set(row.allotmentPaymentId, d);
    });
    return map;
  }, [agreementData]);

  // ✅ Fallback map for Status / Brokerage % / TDS — keyed by allotmentPaymentId,
  // sourced from CpPayments (BrokerageType = "Basic"). Only consulted when the
  // corresponding field is missing on the raw getAllBrokerageData record.
  const cpPaymentsFallbackMap = useMemo(() => {
    const map = new Map();

    (cpPaymentsFallback || []).forEach((row) => {
      map.set(row.allotmentPaymentId, row);
    });

    return map;
  }, [cpPaymentsFallback]);

  // Agreement Value always comes from AllotmentPayment.unitCost.bsp, for every
  // FY. booking_value is only used as a last-resort safety net if the unit
  // cost record can't be found at all.
  const getAgreementValue = (rec) => {
    const allotmentId = rec.allotment_payment_id || rec.allotmentPaymentId;
    const bsp = unitCostMap.get(allotmentId);
    if (bsp > 0) return bsp;

    const bookingValue = toNumber(rec.booking_value);
    if (bookingValue > 0) return bookingValue;

    // ✅ Older bookings: unitCost.bsp was never backfilled and the API's
    // booking_value is also 0 — read it off CpPayments instead.
    const fallback = cpPaymentsFallbackMap.get(allotmentId);
    return toNumber(fallback?.Agreement_Value);
  };

  // ✅ Basic brokerage: prefer getAllBrokerageData's rec.basic, fall back to
  // CpPayments.Brokerage_Amount_Basic when the API sends 0 (or a "0"-ish value).
  const getBasicBrokerage = (rec) => {
    const basicNum = toNumber(rec.basic);
    if (basicNum > 0) return basicNum;
    const allotmentId = rec.allotment_payment_id || rec.allotmentPaymentId;
    const fallback = cpPaymentsFallbackMap.get(allotmentId);
    return toNumber(fallback?.Brokerage_Amount_Basic);
  };

  // ✅ SECOND: cpMap (now safe to use enrichmentMap)
  const cpMap = useMemo(() => {
    const map = new Map();

    (allData || []).forEach((rec) => {
      const id = rec.cp_id || `__no_cp__${rec.project_id}`;
      const name = rec.cp_name || "Unknown CP";

      if (!map.has(id)) {
        map.set(id, {
          cp_id: id,
          name,
          units: 0,
          value: 0,
          brokerage: 0,
          aop: 0,
          pop: 0,
          total: 0,
          paid: 0,
          records: [],
        });
      }

      const e = map.get(id);

      const allotmentId =
        rec.allotment_payment_id || rec.allotmentPaymentId;

      const enriched = enrichmentMap.get(allotmentId);

      e.units += 1;
      e.value += getAgreementValue(rec);
      e.brokerage += getBasicBrokerage(rec);
      e.aop += toNumber(rec.aop);
      e.pop += toNumber(rec.pop);
      e.total += toNumber(rec.total_brokerage);

      // ✅ safe now
      e.paid += toNumber(enriched?.paid_amount ?? 0);
      e.payment_date = enriched?.last_payment_date || null;

      e.records.push(rec);
    });

    return map;
  }, [allData, enrichmentMap, unitCostMap, cpPaymentsFallbackMap]);

  /** Sorted + searched flat rows for the table */
  const tableRows = useMemo(() => {
    let rows = Array.from(cpMap.values());

    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter((r) => r.name.toLowerCase().includes(q));
    }

    if (sortBy) {
      rows = [...rows].sort((a, b) => {
        const diff = toNumber(a[sortBy]) - toNumber(b[sortBy]);
        return sortOrder === "asc" ? diff : -diff;
      });
    }

    return rows;
  }, [cpMap, search, sortBy, sortOrder]);

  /** Current page slice */
  const pagedRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return tableRows.slice(start, start + PAGE_SIZE);
  }, [tableRows, page]);

  const totalPages = Math.ceil(tableRows.length / PAGE_SIZE);

  /** Summary card totals */
  const summary = useMemo(() => {
    let totalBasic = 0,
      totalPaid = 0,
      totalAOP = 0,
      totalPOP = 0;

    (allData || []).forEach((rec) => {
      const allotmentId =
        rec.allotment_payment_id || rec.allotmentPaymentId;

      const enriched = enrichmentMap.get(allotmentId);

      totalBasic += getBasicBrokerage(rec);
      totalAOP += toNumber(rec.aop);
      totalPOP += toNumber(rec.pop);

      // ✅ MERGED FROM RPC
      totalPaid += toNumber(enriched?.paid_amount ?? 0);
    });

    const totalAll = totalBasic + totalAOP + totalPOP || 1;

    return {
      totalBasic,
      totalPaid,
      totalAOP,
      totalPOP,

      basicPct: safePercent(totalBasic, totalAll),
      paidPct: safePercent(totalPaid, totalAll),
      aopPct: safePercent(totalAOP, totalAll),
      popPct: safePercent(totalPOP, totalAll),
    };
  }, [allData, enrichmentMap, cpPaymentsFallbackMap]);

  // ✅ AOP GROUPING (TOP LEVEL)
  const aopProjectRows = useMemo(() => {
    if (!selectedCp?.cp_id) return [];

    const entry = cpMap.get(selectedCp.cp_id);
    if (!entry?.records?.length) return [];

    const planMap = new Map();

    entry.records.forEach((rec) => {
      console.log("Processing record for AOP grouping:", rec);
      const aopValue = toNumber(rec.aop);
      if (aopValue <= 0) return;

      const planId = rec.aop_plan_id || "no-plan";

      const allotmentId =
        rec.allotment_payment_id || rec.allotmentPaymentId;

      const enriched = enrichmentMap.get(allotmentId);

      if (!planMap.has(planId)) {
        planMap.set(planId, {
          aop_plan_id: rec.aop_plan_id || null,
          aopPlanName:
            enriched?.aop_plan_name ||
            rec.aop_plan_name ||
            rec.aopPlanName ||
            rec.plan_name ||
            "Unnamed Plan",
          total: 0,
          projects: new Map(),
        });
      }
      const plan = planMap.get(planId);
      plan.total += aopValue;

      const projectId = rec.project_id;


      if (!plan.projects.has(projectId)) {
        plan.projects.set(projectId, {
          project_id: projectId,
          projectName: rec.project_name,
          amount: 0,
          sold_value: 0,
          saleable_area: 0,
        });
      }

      const proj = plan.projects.get(projectId);
      proj.amount += aopValue;
      proj.sold_value += toNumber(rec.booking_value);


      proj.saleable_area += toNumber(
        enriched?.saleable_area ??
        rec.saleable_area ??
        rec.saleableArea ??
        rec.area ??
        0
      );
    });

    return Array.from(planMap.values()).flatMap((plan) =>
      Array.from(plan.projects.values()).map((proj) => ({
        aop_plan_id: plan.aop_plan_id,
        aopPlanName: plan.aopPlanName,

        project_id: proj.project_id,
        projectName: proj.projectName,

        amount: proj.amount,
        sold_value: proj.sold_value,
        saleable_area: proj.saleable_area,
      }))
    );
  }, [selectedCp, cpMap]);

  // ✅ POP GROUPING (TOP LEVEL)
  const popProjectRows = useMemo(() => {
    if (!selectedCp?.cp_id) return [];

    const entry = cpMap.get(selectedCp.cp_id);
    if (!entry?.records?.length) return [];

    const planMap = new Map();

    entry.records.forEach((rec) => {
      const popValue = toNumber(rec.pop);
      if (popValue <= 0) return;

      const planId = rec.pop_plan_id || "no-plan";
      const allotmentId =
        rec.allotment_payment_id || rec.allotmentPaymentId;

      const enriched = enrichmentMap.get(allotmentId);

      if (!planMap.has(planId)) {
        planMap.set(planId, {
          pop_plan_id: rec.pop_plan_id || null,
          popPlanName:
            enriched?.pop_plan_name || rec.pop_plan_name || rec.popPlanName || rec.plan_name || "Unnamed Plan",
          total: 0,
          projects: new Map(),
        });
      }

      const plan = planMap.get(planId);
      plan.total += popValue;

      const projectId = rec.project_id;

      if (!plan.projects.has(projectId)) {
        plan.projects.set(projectId, {
          project_id: projectId,
          projectName: rec.project_name,
          amount: 0,
          sold_value: 0,
          saleable_area: 0,
        });
      }

      const proj = plan.projects.get(projectId);
      proj.amount += popValue;
      proj.sold_value += toNumber(rec.booking_value);
      proj.saleable_area += toNumber(
        enriched?.saleable_area ??
        rec.saleable_area ??
        rec.saleableArea ??
        rec.area ??
        0
      );
    });

    return Array.from(planMap.values()).flatMap((plan) =>
      Array.from(plan.projects.values()).map((proj) => ({
        pop_plan_id: plan.pop_plan_id,
        popPlanName: plan.popPlanName,

        project_id: proj.project_id,
        projectName: proj.projectName,

        amount: proj.amount,
        sold_value: proj.sold_value,
        saleable_area: proj.saleable_area,
      }))
    );
  }, [selectedCp, cpMap]);
  /** Drawer data for the selected CP — instantly from memory */
  const drawerData = useMemo(() => {
    if (!selectedCp?.cp_id) {
      return { bookingRows: [] };
    }

    const entry = cpMap.get(selectedCp.cp_id);

    if (!entry?.records?.length) {
      return { bookingRows: [] };
    }

    const records = entry.records;

    const bookingRows = records.map((rec) => {
      const allotmentId =
        rec.allotment_payment_id || rec.allotmentPaymentId;

      const enriched = enrichmentMap.get(allotmentId);
      const fallback = cpPaymentsFallbackMap.get(allotmentId);
      const agreementValue = getAgreementValue(rec);

      // ✅ Coerce to number BEFORE checking for "missing" — the backend can send
      // percentage/TDS as numeric-looking strings ("0", "0.00"), which are
      // truthy in JS and would otherwise block the CpPayments fallback via `||`/`??`.
      const tdsNum = toNumber(rec?.Brokerage_Amount_TDS);
      const percentNum = toNumber(rec.Brokerage_Persentage);

      return {
        id: allotmentId,
        allotmentPaymentId: allotmentId,
        project_id: rec.project_id,
        projectName: rec.project_name,
        project_short_code: projectShortCodeMap.get(rec.project_id) || null,
        unit_no: rec.unit_no,
        wing_name: rec.wing_name,
        // Lead Agreement tab extras:
        unit_of: (() => {
          const meta = bookingMetaMap.get(allotmentId);
          return meta?.unitId ? unitOfMap.get(meta.unitId) || null : null;
        })(),
        agreement_date: agreementDateMap.get(allotmentId) || null,
        booking_date: bookingMetaMap.get(allotmentId)?.bookingDate || rec.created_at || null,
        booking_value: toNumber(rec.booking_value),
        brokerage_basic: getBasicBrokerage(rec),
        aop: toNumber(rec.aop),
        pop: toNumber(rec.pop),
        aop_plan_name: enriched?.aop_plan_name || rec.aop_plan_name || rec.aopPlanName || null,
        pop_plan_name: enriched?.pop_plan_name || rec.pop_plan_name || rec.popPlanName || null,
        total_brokerage: toNumber(rec.total_brokerage),
        created_at: rec.created_at,

        invoice_url: enriched?.system_invoice_url || rec.invoice_url,
        invoice_number: enriched?.cp_invoice_number || rec.invoice_number,
        invoice_date: enriched?.cp_invoice_date || rec.invoice_date,

        payment_logs: enriched?.logs ?? [],
        paid_amount: toNumber(enriched?.paid_amount ?? 0),
        payment_date: enriched?.last_payment_date || null,

        status: rec.Status || fallback?.Status || null,
        tds_amount: tdsNum > 0 ? tdsNum : toNumber(fallback?.Brokerage_Amount_TDS),
        agreement_value: agreementValue,
        brokerage_percentage: percentNum > 0 ? percentNum : toNumber(fallback?.Brokerage_Persentage),

        fvPayments: [],
      };
    });

    // ✅ Merge in agreement-done leads that getAllBrokerageData did NOT return
    // (i.e. no CP-payment entry yet) so the drawer shows the CP's complete list.
    // These carry ₹0 brokerage and a null status → "Not Created" chip in the UI.
    const knownIds = new Set(bookingRows.map((r) => r.allotmentPaymentId));
    const extraRows = (cpFullLeads || [])
      .filter((b) => b.allotmentPaymentId && !knownIds.has(b.allotmentPaymentId))
      .map((b) => {
        const inv = b.Inventory || {};
        const proj = inv.Projects || {};
        const wing = inv.Wings || {};
        const agrArr = Array.isArray(b.AgreementForm) ? b.AgreementForm : [];
        const agr = agrArr.find((a) => a?.agreementStatus?.agreement_date) || agrArr[0] || null;
        const bsp = toNumber(b?.unitCost?.bsp ?? b?.unitcost?.bsp);
        return {
          id: b.allotmentPaymentId,
          allotmentPaymentId: b.allotmentPaymentId,
          project_id: inv.projectId || null,
          projectName: proj.projectName || null,
          project_short_code: proj.projectShortCode || null,
          unit_no: inv.unitNo || null,
          wing_name: wing.wingName || null,
          unit_of: inv.unitOf || null,
          agreement_date: agr?.agreementStatus?.agreement_date || null,
          booking_date: b.preselectConfirmationDate || b.created_at || null,
          booking_value: bsp,
          agreement_value: bsp,
          brokerage_basic: 0,
          aop: 0,
          pop: 0,
          aop_plan_name: null,
          pop_plan_name: null,
          total_brokerage: 0,
          created_at: b.created_at,
          invoice_url: null,
          invoice_number: null,
          invoice_date: null,
          payment_logs: [],
          paid_amount: 0,
          payment_date: null,
          status: null, // → "Not Created" chip
          tds_amount: 0,
          brokerage_percentage: 0,
          fvPayments: [],
          _noCpPayment: true,
        };
      });

    return { bookingRows: [...bookingRows, ...extraRows] };
  }, [selectedCp, cpMap, enrichmentMap, unitCostMap, cpPaymentsFallbackMap, projectShortCodeMap, bookingMetaMap, unitOfMap, agreementDateMap, cpFullLeads]);

  /** bookingSummary counts for the drawer header cards */
  const drawerBookingSummary = useMemo(() => {
    if (!selectedCp?.cp_id) return {};
    const rows = drawerData.bookingRows;
    return {
      totalInvoices: rows.length,
      pendingInvoiceCount: rows.filter((r) => !r.invoice_url).length,
      inProcessCount: rows.filter((r) => r.invoice_url && toNumber(r.paid_amount) <= 0).length,
      paymentDoneCount: rows.filter((r) => toNumber(r.paid_amount) > 0).length,
    };
  }, [selectedCp, drawerData]);

  // ─── Filter panel CP search ───────────────────────────────────────────────
  const filteredCpOptions = useMemo(() => {
    return allCpOptions.filter((cp) =>
      cp.name.toLowerCase().includes(cpFilterSearch.toLowerCase())
    );
  }, [allCpOptions, cpFilterSearch]);

  // ─── Handlers ────────────────────────────────────────────────────────────────
  const handleSort = (column) => {
    if (!column) return;
    if (sortBy === column) setSortOrder((p) => (p === "asc" ? "desc" : "asc"));
    else { setSortBy(column); setSortOrder("desc"); }
  };

  const handlePageChange = (_, value) => setPage(value);

  const handleCpToggle = (cpId) => () => {
    setTempSelectedCpIds((prev) =>
      prev.includes(cpId) ? prev.filter((id) => id !== cpId) : [...prev, cpId]
    );
  };

  const handleFilterApply = () => {
  
    setSelectedCpIds(tempSelectedCpIds);
    setPage(1);
    const parts = [];
   if (fyFilter !== "All") parts.push(fyFilter);
    if (tempSelectedCpIds.length > 0) parts.push(`CP (${tempSelectedCpIds.length})`);
    setBtnName(parts.length ? parts.join(", ") : "None");
    setFilterOpen(false);
  };

  const handleFilterClear = () => {
    const fy = getCurrentFY();
    setTempFyFilter(fy); setFyFilter(fy);
    setTempSelectedCpIds([]); setSelectedCpIds([]);
    setBtnName("None");
    setPage(1);
    setFilterOpen(false);
  };

  const handleOpenDrawer = (row) => { setSelectedCp({ ...row }); setOpen(true); };
  const handleCloseDrawer = () => { setOpen(false); setSelectedCp(null); };

  // ─── Render ───────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box
        minHeight="100vh"
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <CircularProgress size={50} />
      </Box>
    );
  }

  return (
    <Box p={3} minHeight="100vh">
      {/* ── Header ── */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography fontSize={20} fontWeight={700} lineHeight={1.2}>CP Payout</Typography>
          <Typography fontSize={12} color={COLORS.textSecondary} mt={0.3}>
            Channel Partner payout overview & tracking
          </Typography>
        </Box>

        <Box display="flex" gap={1.5} alignItems="center">
          {/* Filter dropdown */}
          <Box sx={{ position: "relative" }}>
            <Typography fontSize={12} color={COLORS.textSecondary} mb={0.5}>Filter by</Typography>
            <Box
              onClick={() => {
                setTempFyFilter(fyFilter);
                setTempSelectedCpIds(selectedCpIds);
                setCpFilterSearch("");
                setFilterOpen((p) => !p);
              }}
              sx={{
                minWidth: 180, px: 2, py: 1.2,
                border: `1px solid ${COLORS.border}`, borderRadius: 2,
                bgcolor: "#fff", cursor: "pointer",
                display: "flex", justifyContent: "space-between", alignItems: "center",
                fontSize: 14, fontWeight: 500,
              }}
            >
              <span>{btnName}</span>
              <ExpandMoreIcon fontSize="small" />
            </Box>

            {filterOpen && (
              <Box
                sx={{
                  position: "absolute", top: "110%", right: 0, width: 500,
                  bgcolor: "#fff", border: `1px solid ${COLORS.border}`, borderRadius: 3,
                  boxShadow: "0 10px 30px rgba(0,0,0,0.08)", zIndex: 20, overflow: "hidden",
                }}
              >
                <Box sx={{ display: "flex", minHeight: 380 }}>
                  {/* Left tabs */}
                  <Box sx={{ width: "38%", borderRight: `1px solid ${COLORS.border}` }}>
                    <List sx={{ p: 0 }}>
                      {["CP"].map((item) => (
                        <ListItem key={item} disablePadding>
                          <ListItemButton onClick={() => setFilterTab(item)}>
                            <ListItemText primary={item} />
                            {filterTab === item && (
                              <ListItemIcon sx={{ minWidth: "auto" }}>
                                <CheckIcon color="primary" />
                              </ListItemIcon>
                            )}
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Box>

                  {/* Right panel */}
                  <Box sx={{ width: "62%", bgcolor: "#f9fafb", p: 1, maxHeight: 380, overflowY: "auto" }}>
                    {filterTab === "CP" && (
                      <>
                        <SearchBar
                          placeholder="Search CP"
                          search={(e) => setCpFilterSearch(e.target.value)}
                          width="100%"
                          id="cp-filter-search"
                        />
                        {allCpOptions.length === 0 ? (
                          <Box sx={{ height: 300, display: "flex", alignItems: "center", justifyContent: "center", color: COLORS.textSecondary }}>
                            No Data Available
                          </Box>
                        ) : (
                          <List>
                            <ListItem disablePadding>
                              <ListItemButton dense onClick={() => setTempSelectedCpIds([])}>
                                <ListItemIcon sx={{ minWidth: "auto", mr: 1 }}>
                                  <Checkbox edge="start" checked={tempSelectedCpIds.length === 0} tabIndex={-1} disableRipple />
                                </ListItemIcon>
                                <ListItemText primary="None" />
                              </ListItemButton>
                            </ListItem>
                            {filteredCpOptions.length === 0 ? (
                              <Box sx={{ py: 4, textAlign: "center", color: COLORS.textSecondary, fontSize: 14 }}>No matching CP found</Box>
                            ) : (
                              filteredCpOptions.map((cp) => (
                                <ListItem key={cp.id} disablePadding>
                                  <ListItemButton dense onClick={handleCpToggle(cp.id)}>
                                    <ListItemIcon sx={{ minWidth: "auto", mr: 1 }}>
                                      <Checkbox edge="start" checked={tempSelectedCpIds.includes(cp.id)} tabIndex={-1} disableRipple />
                                    </ListItemIcon>
                                    <ListItemText primary={cp.name} />
                                  </ListItemButton>
                                </ListItem>
                              ))
                            )}
                          </List>
                        )}
                      </>
                    )}


                  </Box>
                </Box>

                {/* Footer */}
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, p: 2, borderTop: `1px solid ${COLORS.border}`, bgcolor: "#f9fafb" }}>
                  <Box onClick={handleFilterClear} sx={{ px: 2.5, py: 1, borderRadius: 2, bgcolor: "#fff", border: `1px solid ${COLORS.border}`, cursor: "pointer", fontSize: 14, fontWeight: 500 }}>Clear</Box>
                  <Box onClick={handleFilterApply} sx={{ px: 2.5, py: 1, borderRadius: 2, bgcolor: COLORS.blue, color: "#fff", cursor: "pointer", fontSize: 14, fontWeight: 500 }}>Apply</Box>
                </Box>
              </Box>
            )}
          </Box>

          {/* Search */}
          <Box sx={{ position: "relative" }}>
            <Typography fontSize={12} color={COLORS.textSecondary} mb={0.5}>Search</Typography>
            <SearchBar
              placeholder="Search CP"
              search={(e) => { setSearch(e.target.value); setPage(1); }}
              width="260px"
              id="cp-search"
            />
          </Box>
          {/* FY Dropdown */}
          <Box>
            <Typography fontSize={12} color={COLORS.textSecondary} mb={0.5}>
              Financial Year
            </Typography>

            <Box
              component="select"
              value={fyFilter}
              onChange={(e) => {
                setFyFilter(e.target.value);
                setPage(1);
              }}
              sx={{
                minWidth: 190,
                px: 1.5,
                py: 1.2,
                border: `1px solid ${COLORS.border}`,
                borderRadius: 2,
                fontSize: 14,
                fontWeight: 500,
                bgcolor: "#fff",
                outline: "none",
                cursor: "pointer",
              }}
            >
              {fyOptions.map((fy) => (
                <option key={fy} value={fy}>
                  {fy}
                </option>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>


      <>
        {/* ── Summary Cards ── */}
        <Grid container spacing={2} mb={3}>
          {[
            // Each card uses its own metric's colour token, so all four stay
            // visually distinct (blue / green / violet / amber) and the card
            // colour matches the table column it summarises.
            { title: "Total Basic Brokerage", value: formatCurrency(summary.totalBasic), percent: summary.basicPct, iconBg: COLORS.base, icon: CurrencyRupeeIcon },
            { title: "Paid Till Date", value: formatCurrency(summary.totalPaid), percent: summary.paidPct, iconBg: COLORS.total, icon: TrendingUpIcon },
            { title: "AOP Achieved", value: formatCurrency(summary.totalAOP), percent: summary.aopPct, iconBg: COLORS.aop, icon: TrackChangesIcon },
            { title: "POP Achieved", value: formatCurrency(summary.totalPOP), percent: summary.popPct, iconBg: COLORS.pop, icon: EmojiEventsOutlinedIcon },
          ].map((card) => (
            <Grid item xs={12} sm={6} md={3} key={card.title}>
              <StatCard {...card} />
            </Grid>
          ))}
        </Grid>


      </>


      {/* ── Table ── */}
      <Card elevation={0} sx={{ border: `1px solid ${COLORS.border}`, borderRadius: 3, overflow: "hidden" }}>

        <TableContainer sx={{ maxHeight: "72vh" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ bgcolor: "#f9fafb", height: 60 }}>
                {TABLE_HEADERS.map((col) => (
                  <TableCell
                    key={col.label}
                    sx={{
                      fontSize: 14, fontWeight: 700, color: COLORS.textSecondary,
                      borderBottom: `1px solid ${COLORS.border}`, py: 1.5, bgcolor: "#f9fafb",
                      ...(col.label === "CP NAME" && { width: 220, minWidth: 220, maxWidth: 220 }),
                    }}
                  >
                    {col.key ? (
                      <TableSortLabel
                        active={sortBy === col.key}
                        direction={sortBy === col.key ? sortOrder : "asc"}
                        onClick={() => handleSort(col.key)}
                        sx={{
                          fontSize: 14, fontWeight: 700,
                          color: `${COLORS.textSecondary} !important`,
                          "& .MuiTableSortLabel-icon": { color: `${COLORS.textSecondary} !important` },
                          "&.Mui-active": { color: `${COLORS.blue} !important` },
                          "&.Mui-active .MuiTableSortLabel-icon": { color: `${COLORS.blue} !important` },
                        }}
                      >
                        {col.label}
                      </TableSortLabel>
                    ) : col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {pagedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={TABLE_HEADERS.length} align="center" sx={{ py: 5, color: COLORS.textSecondary, fontSize: 14 }}>
                    No data found
                  </TableCell>
                </TableRow>
              ) : (
                pagedRows.map((row, i) => (
                  <TableRow
                    key={row.cp_id || i}
                    hover
                    sx={{
                      "&:hover": { bgcolor: "#f8fafc" },
                      "& td": { borderBottom: `1px solid ${COLORS.border}`, py: 1.8, fontSize: 14, whiteSpace: "nowrap", verticalAlign: "top" },
                    }}
                  >
                    <TableCell sx={{ width: 220, minWidth: 220, maxWidth: 220, whiteSpace: "normal !important", wordBreak: "break-word" }}>
                      <Typography fontWeight={500} fontSize={14} sx={{ whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.4 }}>
                        {row.name || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{formatIndianNumber(row.units)}</TableCell>
                    <TableCell sx={{ fontWeight: 500, color: COLORS.soldValue }}>{formatCrLakh(row.value)}</TableCell>
                    <TableCell sx={{ fontWeight: 500, color: COLORS.base }}>{formatCrLakh(row.brokerage)}</TableCell>
                    <TableCell sx={{ fontWeight: 500, color: COLORS.aop }}>{formatCrLakh(row.aop)}</TableCell>
                    <TableCell sx={{ fontWeight: 500, color: COLORS.pop }}>{formatCrLakh(row.pop)}</TableCell>
                    <TableCell sx={{ fontWeight: 500, color: COLORS.total }}>{formatCrLakh(row.total)}</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{formatCrLakh(row.paid)}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDrawer(row)}
                        sx={{
                          color: COLORS.textSecondary, border: `1px solid ${COLORS.border}`,
                          borderRadius: 2, p: 0.8,
                          "&:hover": { color: COLORS.blue, bgcolor: "#eff6ff", borderColor: COLORS.blue },
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

      </Card>

      {/* ── Pagination ── */}
      {!loading && totalPages > 1 && (
        <Box mt={3} display="flex" justifyContent="center">
          <Stack spacing={2} alignItems="center">
            <Pagination
              count={totalPages} page={page} onChange={handlePageChange}
              color="primary" shape="rounded" siblingCount={1} boundaryCount={1}
              showFirstButton showLastButton
              sx={{
                "& .MuiPagination-ul": { gap: 1 },
                "& .MuiPaginationItem-root": { fontSize: "16px", fontWeight: 600, minWidth: 42, height: 42, borderRadius: "10px" },
              }}
            />
          </Stack>
        </Box>
      )}

      {/* ── Drawer ── */}
      <CpPayoutDrawer
        open={open}
        onClose={handleCloseDrawer}
        fyFilter={fyFilter}
        selectedCp={{
          ...selectedCp,
          bookingSummary: drawerBookingSummary,
          aopProjectRows: aopProjectRows,
          popProjectRows: popProjectRows,
        }}
        bookingRows={drawerData.bookingRows}
        loading={false}
        fetchingMore={false}
        hasMore={false}
        onLoadMore={() => { }}
        onDeleted={fetchBrokerageData}
      />
    </Box>
  );
}

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CpPayout />
    </ThemeProvider>
  );
}